/**
 * Converte lista plana de slots do chatbot em nós e arestas para React Flow,
 * com layout hierárquico (raiz à esquerda, filhos à direita).
 * Usa altura da subárvore por nó para evitar sobreposição.
 * Gaps reduzidos para o grafo ficar mais compacto e os nós maiores na tela.
 */
import { countHiddenDescendants } from "./chatbotFlowCollapse";
import { slotStepName } from "../../utils/chatbotNavigationActions";
import { isFlowStartSlot } from "./chatbotSlotParentUtils";

const NODE_WIDTH = 260;
const NODE_HEIGHT = 100;
const HORIZONTAL_GAP = 72;
const VERTICAL_GAP = 56;

export function slotsToFlow(slots, options = {}) {
  const collapsedIds = options.collapsedIds || new Set();
  const onToggleCollapse = options.onToggleCollapse || null;

  if (!Array.isArray(slots) || slots.length === 0) {
    return { nodes: [], edges: [], slotMap: new Map() };
  }

  const slotMap = new Map(slots.map((s) => [String(s.id), s]));

  const byParent = new Map();
  slots.forEach((s) => {
    const pid = s.id_pai != null ? String(s.id_pai) : "0";
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid).push(s);
  });

  const nodes = [];
  const edges = [];

  /** Retorna a altura vertical necessária para a subárvore deste nó (sem sobrepor irmãos). */
  function subtreeHeight(slotId) {
    if (collapsedIds.has(String(slotId))) return NODE_HEIGHT;
    const children = byParent.get(String(slotId)) || [];
    if (children.length === 0) return NODE_HEIGHT;
    const childHeights = children.map((c) => subtreeHeight(c.id));
    const sum = childHeights.reduce((a, b) => a + b, 0);
    return sum + (children.length - 1) * VERTICAL_GAP;
  }

  function layout(parentId, level, baseY) {
    const pid = parentId === null || parentId === undefined ? "0" : String(parentId);
    const children = byParent.get(pid) || [];
    if (children.length === 0) return 0;

    const startX = level * (NODE_WIDTH + HORIZONTAL_GAP);
    let currentY = baseY;

    children.forEach((slot) => {
      const id = String(slot.id);
      const isCollapsed = collapsedIds.has(id);
      const childList = byParent.get(id) || [];
      const hasChildren = childList.length > 0;
      const height = subtreeHeight(slot.id);
      const nodeY = currentY + height / 2 - NODE_HEIGHT / 2;

      nodes.push({
        id,
        type: "chatbot",
        position: { x: startX, y: nodeY },
        data: {
          ...slot,
          id: slot.id,
          label: slotStepName(slot),
          hasChildren,
          isCollapsed,
          hiddenDescendantCount: isCollapsed
            ? countHiddenDescendants(id, collapsedIds, byParent)
            : 0,
          isFlowStart: isFlowStartSlot(slot, slots),
          onToggleCollapse,
        },
      });

      if (pid !== "0") {
        edges.push({
          id: `e-${pid}-${id}`,
          source: pid,
          target: id,
        });
      }

      if (!isCollapsed) {
        layout(slot.id, level + 1, currentY);
      }

      currentY += height + VERTICAL_GAP;
    });

    return currentY - baseY;
  }

  layout(null, 0, 0);

  return { nodes, edges, slotMap };
}
